import { useEffect, useRef, useState } from "react";
import { X, UserPlus, User, ChevronRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

export const LinkedGuestsEditor = ({ value = [], onChange, exclude, onOpen, testid = "linked" }) => {
  const [names, setNames] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => { api.guestNames().then(setNames).catch(() => {}); }, []);
  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const linked = value || [];
  const add = (n) => {
    const name = String(n || "").trim();
    if (!name || name === exclude || linked.includes(name)) { setQ(""); setOpen(false); return; }
    onChange([...linked, name]);
    setQ(""); setOpen(false);
  };
  const remove = (n) => onChange(linked.filter((x) => x !== n));

  const suggestions = names
    .filter((n) => n && n !== exclude && !linked.includes(n) && (!q || n.toLowerCase().includes(q.toLowerCase())))
    .slice(0, 8);

  return (
    <div ref={boxRef} className="relative">
      {linked.length > 0 && (
        <div className="mb-2 space-y-2">
          {linked.map((n) => (
            <div key={n} className="flex items-center gap-2" data-testid={`${testid}-chip-${n}`}>
              {onOpen ? (
                <button
                  type="button"
                  onClick={() => onOpen(n)}
                  title={`Open ${n}'s profile`}
                  aria-label={`Open ${n}'s profile`}
                  className="group flex min-w-0 flex-1 items-center gap-2.5 rounded-xl border border-white/10 bg-white/5 px-3.5 py-2.5 text-left transition-colors duration-200 hover:border-[#E0575B]/40 hover:bg-[#9B2226]/10"
                  data-testid={`${testid}-open-${n}`}
                >
                  <User className="h-4 w-4 shrink-0 text-[#E0575B]" />
                  <span className="min-w-0 flex-1 truncate text-sm font-600 text-zinc-100">{n}</span>
                  <span className="inline-flex shrink-0 items-center gap-0.5 text-xs text-zinc-500 transition-colors group-hover:text-[#E0575B]">View <ChevronRight className="h-3.5 w-3.5" /></span>
                </button>
              ) : (
                <div className="flex min-w-0 flex-1 items-center gap-2.5 rounded-xl border border-white/10 bg-white/5 px-3.5 py-2.5" data-testid={`${testid}-name-${n}`}>
                  <User className="h-4 w-4 shrink-0 text-[#E0575B]" />
                  <span className="min-w-0 flex-1 truncate text-sm font-600 text-zinc-100">{n}</span>
                </div>
              )}
              <button type="button" onClick={() => remove(n)} title={`Unlink ${n}`} aria-label={`Unlink ${n}`} className="shrink-0 rounded-lg p-2 text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-red-400" data-testid={`${testid}-remove-${n}`}>
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="flex items-center gap-2">
        <Input
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(q); } }}
          placeholder="Add a guest by name…"
          className="min-w-0 flex-1 border-zinc-800 bg-zinc-900 focus-visible:ring-[#E0575B]/70"
          data-testid={`${testid}-input`}
        />
        <Button type="button" onClick={() => add(q)} variant="outline" size="sm" className="shrink-0 border-zinc-700 bg-transparent" data-testid={`${testid}-add-btn`}>
          <UserPlus className="mr-1.5 h-4 w-4" /> Link
        </Button>
      </div>
      {open && suggestions.length > 0 && (
        <div className="absolute inset-x-0 z-50 mt-1 max-h-56 overflow-y-auto rounded-md border border-zinc-800 bg-zinc-950 py-1 shadow-xl" data-testid={`${testid}-suggestions`}>
          {suggestions.map((n) => (
            <button key={n} type="button" onMouseDown={(e) => { e.preventDefault(); add(n); }} className="block w-full px-3 py-1.5 text-left text-sm text-zinc-300 hover:bg-white/5" data-testid={`${testid}-sugg-${n}`}>
              {n}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
